import { Injectable, Inject } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { merge, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { APP_CONFIG } from '../app.config';
import { AppConfig } from '../models/config/app-config';
import { DocumentService } from './document.service';
import { AppService } from './app.service';
import { ScannedDokument } from '../components/visum-erfassung/dokumente/scanned.document';

@Injectable({ providedIn: 'root' })
export class DokumentValidationService {
  constructor(
    @Inject(APP_CONFIG) readonly appConfig: AppConfig,
    readonly documentService: DocumentService,
    readonly appService: AppService
  ) {}

  private get isCVisumMode(): boolean {
    try {
      if (typeof (this.appService as any)?.isCVisum === 'function' && this.appService.isCVisum()) return true;
    } catch {}
    const raw = (this.appConfig as any)?.visumAntragsartEnum ?? (this.appConfig as any)?.visumAntragsart ?? '';
    const v = String(raw).toUpperCase().trim();
    return v === 'KURZFRISTIGER_AUFENTHALT' || v === 'C' || v === 'VISUM_C';
  }

  dokumentartRequired(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      if (this.isCVisumMode) return null;
      const parent = c.parent as FormGroup | null;
      const docs = parent?.get('pdfDocuments')?.value as ScannedDokument[] | null | undefined;
      if (Array.isArray(docs) && docs.length > 0) return null;
      const v = (c.value ?? '').toString().trim();
      return v ? null : { dokumentartRequired: true };
    };
  }

  technicalDocsValidator(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      if (this.isCVisumMode) return null;
      const docs = c.value as ScannedDokument[] | null | undefined;
      if (!Array.isArray(docs) || docs.length === 0) return null;
      try {
        const err = this.documentService.validateDocuments(c);
        if (!err) return null;
        const { required, noDocuments, ...rest } = err as any;
        return Object.keys(rest).length ? rest : null;
      } catch { return null; }
    };
  }

  businessGroupValidator(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!(ctrl instanceof FormGroup)) return null;
      if (this.isCVisumMode) return null;

      const g = ctrl;
      const docs = (g.get('pdfDocuments')?.value as ScannedDokument[]) ?? [];
      const hasDocs = Array.isArray(docs) && docs.length > 0;

      const requiresBA = !!g.get('reisegrundErwerbstaetigkeit')?.value;
      if (requiresBA) {
        if (!hasDocs) return { missingBADocument: true };
        const hasBA = this.hasBADocument(docs);
        return hasBA ? null : { missingBADocument: true };
      }
      if (!hasDocs) return { noDocuments: true };
      return null;
    };
  }

  needsBADocument$(form: FormGroup): Observable<boolean> {
    const rg = form.get('reisegrundErwerbstaetigkeit');
    return merge(
      rg.valueChanges.pipe(startWith(rg.value)),
      form.statusChanges.pipe(startWith(form.status))
    ).pipe(
      map(() => !this.isCVisumMode && !!rg.value),
      distinctUntilChanged()
    );
  }

  isDokumentPflicht$(form: FormGroup): Observable<boolean> {
    return form.statusChanges.pipe(
      startWith(form.status),
      map(() => !this.isCVisumMode),
      distinctUntilChanged()
    );
  }

  wireUp(form: FormGroup) {
    const deps = ['reisegrundErwerbstaetigkeit', 'dokumentart', 'pdfDocuments']
      .map(n => form.get(n)).filter(Boolean);
    return merge(...deps.map(d => d.valueChanges))
      .subscribe(() => form.updateValueAndValidity({ onlySelf: false, emitEvent: true }));
  }

  private hasBADocument(docs: ScannedDokument[]): boolean {
    return docs.some(d => {
      if (d?.barelevant != null) return !!d.barelevant;
      try { return this.documentService.isBADocument(d); } catch { return false; }
    });
  }
}
